import { useMemo } from "react";
import { Table, Alert, Skeleton } from "antd";
import { Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Invoice } from "../../../bitmaker/schemas/Billing/Invoice";

import { getInvoiceColumns } from "../InvoiceTableColumns";

interface ClientDetailInvoicesProps {
  invoices: Invoice[];
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  onCreateInvoice: () => void;
  clientDisplayName: string | null;
}

export default function ClientDetailInvoicesList({
  invoices,
  loading,
  error,
  onRetry,
  onCreateInvoice,
  clientDisplayName,
}: ClientDetailInvoicesProps) {
  const navigate = useNavigate();

  const invoiceTableColumns = useMemo(
    () => getInvoiceColumns({ showClient: false }),
    [],
  );

  const isInitialLoading = loading && invoices.length === 0;

  return (
    <div className="border-t bg-white shadow-sm">
      <div className="max-w-4xl mx-auto mt-6 mb-12">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold text-gray-800">
            Invoices for {clientDisplayName || "Client"}
          </h3>

          <button
            type="button"
            onClick={onCreateInvoice}
            className="btn btn-green-2"
            disabled={loading}
          >
            <Plus size={16} />
            <span>Add Invoice</span>
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4">
            <Alert
              type="error"
              showIcon
              message="Failed to load client invoices"
              description="Something went wrong while fetching data. Please try again."
              action={
                <button
                  type="button"
                  className="btn btn-gray"
                  onClick={onRetry}
                  disabled={loading}
                >
                  Retry
                </button>
              }
            />
          </div>
        )}

        {/* Table / Loading */}
        {isInitialLoading ? (
          <div className="mt-4 space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="py-2">
                <Skeleton
                  active
                  title={false}
                  paragraph={{ rows: 1, width: "100%" }}
                />
              </div>
            ))}
          </div>
        ) : (
          <Table
            columns={invoiceTableColumns}
            dataSource={invoices}
            rowKey="id"
            pagination={{ pageSize: 10 }}
            rowClassName={() => "cursor-pointer"}
            onRow={(record: Invoice) => ({
              onClick: () => navigate(`/billing/invoices/${record.id}`),
            })}
            loading={loading}
            locale={{
              emptyText: error ? (
                "Unable to load invoices"
              ) : (
                <div className="py-10 flex flex-col items-center justify-center gap-3 text-center">
                  <div className="text-gray-500 text-sm">
                    This client has no invoices yet
                  </div>
                  <button
                    type="button"
                    className="btn btn-gray"
                    onClick={onCreateInvoice}
                    disabled={loading}
                  >
                    <Plus size={16} />
                    <span>Create invoice</span>
                  </button>
                </div>
              ),
            }}
          />
        )}
      </div>
    </div>
  );
}
